// ============================================================
// pages/admin/AdminCustomers.jsx — Customer Management (Firebase)
// ============================================================

import React, { useState } from "react";
import { useCustomers } from "../../hooks/useFirestore";
import { UserService } from "../../services/firebaseServices";

const tierColors = {
  bronze: "#CD7F32", silver: "#A0A0A0", gold: "#F5A623", platinum: "#8B5CF6",
};

export default function AdminCustomers({ showToast }) {
  const { customers, loading, refresh } = useCustomers();
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const filtered = customers
    .filter((c) => roleFilter === "all" || c.role === roleFilter)
    .filter((c) => (c.displayName || c.email || "").toLowerCase().includes(searchTerm.toLowerCase()));

  const roleCounts = {
    all: customers.length,
    customer: customers.filter((c) => c.role === "customer").length,
    staff: customers.filter((c) => c.role === "staff").length,
    delivery: customers.filter((c) => c.role === "delivery").length,
    admin: customers.filter((c) => c.role === "admin").length,
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handleRoleChange = async (uid, newRole) => {
    try {
      await UserService.updateRole(uid, newRole);
      showToast(`Role updated to ${newRole}`, "success");
      refresh();
    } catch (err) {
      showToast("Failed to update role", "error");
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>User Management</h2>
          <p>View all users — customers, staff, delivery & admins</p>
        </div>
        <div style={{ fontSize: "0.88rem", color: "var(--cocoa)" }}>
          {customers.length} total users
        </div>
      </div>

      {/* Role Filter */}
      <div className="filters">
        {["all", "customer", "staff", "delivery", "admin"].map((r) => (
          <button key={r} className={`filter-btn ${roleFilter === r ? "active" : ""}`}
            onClick={() => setRoleFilter(r)}>
            {r === "all" ? "All" : r.charAt(0).toUpperCase() + r.slice(1)} ({roleCounts[r]})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="filters" style={{ marginBottom: "1.5rem" }}>
        <div className="search-wrap">
          <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: "var(--cocoa)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input className="search-box" placeholder="Search users..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading customers...</p></div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Orders</th>
                <th>Total Spent</th>
                <th>Loyalty</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} style={{ cursor: "pointer" }}
                  onClick={() => setSelectedCustomer(selectedCustomer?.id === c.id ? null : c)}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      {c.photoURL ? (
                        <img src={c.photoURL} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <div style={{
                          width: 36, height: 36, borderRadius: "50%", background: "var(--chai)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "white", fontWeight: 700, fontSize: "0.85rem",
                        }}>
                          {(c.displayName || c.email || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.displayName || "No name"}</div>
                        {!c.isActive && <span className="inline-tag" style={{ background: "#F8D7DA", color: "#721C24" }}>Inactive</span>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: "0.85rem" }}>{c.email}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>{c.phone || "—"}</div>
                  </td>
                  <td>{c.totalOrders || 0}</td>
                  <td style={{ fontWeight: 600 }}>Rs {(c.totalSpent || 0).toLocaleString()}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span className="inline-tag" style={{
                        background: `${tierColors[c.loyaltyTier] || tierColors.bronze}22`,
                        color: tierColors[c.loyaltyTier] || tierColors.bronze,
                        textTransform: "capitalize",
                      }}>
                        {c.loyaltyTier || "bronze"}
                      </span>
                      <span style={{ fontSize: "0.82rem", color: "var(--cocoa)" }}>
                        {c.loyaltyPoints || 0} pts
                      </span>
                    </div>
                  </td>
                  <td>
                    <select className="form-select" value={c.role || "customer"}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleRoleChange(c.id, e.target.value)}
                      style={{ fontSize: "0.82rem", padding: "0.3rem 0.5rem", minWidth: 110 }}>
                      <option value="customer">Customer</option>
                      <option value="staff">Staff</option>
                      <option value="delivery">Delivery</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td style={{ fontSize: "0.85rem", color: "var(--cocoa)" }}>
                    {formatDate(c.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expanded Customer Detail */}
      {selectedCustomer && (
        <div className="card" style={{ marginTop: "1.5rem", padding: "1.5rem" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            Customer Details: {selectedCustomer.displayName}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", fontSize: "0.88rem" }}>
            <div>
              <div style={{ fontWeight: 600, color: "var(--cocoa)", fontSize: "0.78rem", textTransform: "uppercase" }}>Email</div>
              <div>{selectedCustomer.email}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "var(--cocoa)", fontSize: "0.78rem", textTransform: "uppercase" }}>Phone</div>
              <div>{selectedCustomer.phone || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "var(--cocoa)", fontSize: "0.78rem", textTransform: "uppercase" }}>Address</div>
              <div>{selectedCustomer.address || "—"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "var(--cocoa)", fontSize: "0.78rem", textTransform: "uppercase" }}>Date of Birth</div>
              <div>{formatDate(selectedCustomer.dateOfBirth)}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "var(--cocoa)", fontSize: "0.78rem", textTransform: "uppercase" }}>Dietary Restrictions</div>
              <div>{selectedCustomer.preferences?.dietaryRestrictions?.join(", ") || "None"}</div>
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "var(--cocoa)", fontSize: "0.78rem", textTransform: "uppercase" }}>Last Login</div>
              <div>{formatDate(selectedCustomer.lastLoginAt)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}